/**
 * Navigation utility functions for detecting path changes and navigation patterns
 */

/**
 * Checks if a path is related to a specific manufacturer/make
 * @param path URL path to check
 * @returns boolean indicating if the path is a make-specific path
 */
export var isMakePath = function isMakePath(path) {
  return path.startsWith('/autos/') && path.split('/').length > 2;
};

/**
 * Checks if a path is a generic autos path (not make-specific)
 * @param path URL path to check
 * @returns boolean indicating if the path is a generic autos path
 */
export var isGenericAutosPath = function isGenericAutosPath(path) {
  return path === '/autos' || path.endsWith('/autos');
};

/**
 * Checks if navigation is happening between different car makes
 * @param prevPath Previous URL path
 * @param currentPath Current URL path
 * @returns boolean indicating if navigation is between different makes
 */
export var isMakeChangeNavigation = function isMakeChangeNavigation(prevPath, currentPath) {
  if (!prevPath) return false;
  return isMakePath(prevPath) && isMakePath(currentPath) && prevPath !== currentPath;
};

/**
 * Checks if navigation is between similar page types (autos->autos or years->years)
 * but excluding major navigation changes like between different makes
 */
export var isSimilarPageNavigation = function isSimilarPageNavigation(prevPath, currentPath, isCurrentPathAutos, isCurrentPathYears) {
  if (!prevPath) return false;
  var isPreviousPathAutos = prevPath.includes('/autos');
  var isPreviousPathYears = prevPath.includes('/years');

  // Check if this is a navigation between makes
  var isMakeChange = isMakeChangeNavigation(prevPath, currentPath);

  // Check if this is a navigation from generic /autos to specific make
  var isMovingFromGenericToMake = isGenericAutosPath(prevPath) && isMakePath(currentPath);

  // Check if this is a navigation from a make to generic /autos
  var isMovingFromMakeToGeneric = isMakePath(prevPath) && isGenericAutosPath(currentPath);
  return (isPreviousPathAutos && isCurrentPathAutos || isPreviousPathYears && isCurrentPathYears) && !isMakeChange && !isMovingFromGenericToMake && !isMovingFromMakeToGeneric;
};

/**
 * Returns information about navigation between different paths
 * @param prevPath Previous URL path
 * @param currentPath Current URL path
 * @returns Object with navigation change info
 */
export var getNavigationChangeType = function getNavigationChangeType(prevPath, currentPath) {
  if (!prevPath) {
    return {
      isMovingBetweenMakes: false,
      isMovingFromMakeToGeneric: false,
      isMovingFromGenericToMake: false,
      requiresDataClearing: false
    };
  }

  // Using the utility functions for cleaner code
  var isMovingBetweenMakes = isMakeChangeNavigation(prevPath, currentPath);
  var isMovingFromMakeToGeneric = isMakePath(prevPath) && isGenericAutosPath(currentPath);
  var isMovingFromGenericToMake = isGenericAutosPath(prevPath) && isMakePath(currentPath);
  var requiresDataClearing = isMovingBetweenMakes || isMovingFromMakeToGeneric || isMovingFromGenericToMake;
  return {
    isMovingBetweenMakes: isMovingBetweenMakes,
    isMovingFromMakeToGeneric: isMovingFromMakeToGeneric,
    isMovingFromGenericToMake: isMovingFromGenericToMake,
    requiresDataClearing: requiresDataClearing
  };
};

/**
 * Evaluates if navigation requires clearing posts
 * @param prevPath Previous URL path
 * @param currentPath Current URL path
 * @param isCurrentPathAutos Whether current path is autos page
 * @param isCurrentPathYears Whether current path is years page
 * @returns boolean indicating if posts should be cleared
 */
export var shouldClearPosts = function shouldClearPosts(prevPath, currentPath, isCurrentPathAutos, isCurrentPathYears) {
  // Either it's not similar navigation, or it's a make change
  var isSimilarNav = isSimilarPageNavigation(prevPath, currentPath, isCurrentPathAutos, isCurrentPathYears);
  var isMakeChange = isMakeChangeNavigation(prevPath, currentPath);
  return !isSimilarNav || isMakeChange;
};

/**
 * Evaluates if we should scroll to top after navigation
 * @param isFirstLanding Whether this is the first page load
 * @param prevPath Previous URL path
 * @param currentPath Current URL path
 * @param isCurrentPathAutos Whether current path is autos page
 * @param isCurrentPathYears Whether current path is years page
 * @returns boolean indicating if page should scroll to top
 */
export var shouldScrollToTop = function shouldScrollToTop(isFirstLanding, prevPath, currentPath, isCurrentPathAutos, isCurrentPathYears) {
  if (isFirstLanding) return false;
  return !isSimilarPageNavigation(prevPath, currentPath, isCurrentPathAutos, isCurrentPathYears);
};

/**
 * Detects if we should show loading state based on navigation context
 * @param isFirstLanding Whether this is the first page load
 * @param prevPath Previous URL path
 * @param currentPath Current URL path
 * @param isCurrentPathAutos Whether current path is autos page
 * @param isCurrentPathYears Whether current path is years page
 * @param hasPosts Whether we have posts loaded already
 * @returns boolean indicating if loading state should be shown
 */
export var shouldShowLoadingState = function shouldShowLoadingState(isFirstLanding, prevPath, currentPath, isCurrentPathAutos, isCurrentPathYears, hasPosts) {
  if (isFirstLanding) return false;
  var similarNavigation = isSimilarPageNavigation(prevPath, currentPath, isCurrentPathAutos, isCurrentPathYears);
  return !similarNavigation || !hasPosts;
};